/**
 * Online mode glue (docs/design/multiplayer.md §4): connect the ProfileStore to the Worker at
 * boot, and route solo keys through run tickets so the server pays the rewards.
 *
 *   const mode = await connectWithin(4000, store, api, { offline: params.has('offline') });
 *   const t = await startOnlineRun(api, { dungeonId, level, spectate });   // seed + clamped level
 *   const r = await finishOnlineRun(api, store, t, { result, party, … });  // server rewards
 *
 * Local mode (the Vite dev server, `?offline=1`, an unreachable API) never calls these.
 */
import type { BotSkillPreset, ClassId, RunResult } from '@keystone/sim';
import type { ApiClient } from './api';
import type { ProfileStore } from './profileStore';
import type { AutoClaims, RunSummary } from './run';
import { localDateString } from './forge/localDate';

export type OnlineMode = 'online' | 'offline';

/** Stop waiting for the API after this long at boot (the game starts locally). */
export const CONNECT_TIMEOUT_MS = 4000;

/**
 * Health → device token → server profile (created, or uploaded once from the local save) →
 * store.goOnline. Any failure leaves the store local.
 */
export async function connectOnline(store: ProfileStore, api: ApiClient, opts: { offline?: boolean; cancelled?: () => boolean } = {}): Promise<OnlineMode> {
  if (opts.offline) return 'offline';
  if (!(await api.available())) return 'offline';
  try {
    await api.ensureDevice();
    let got = await api.getProfile();
    if (got.status === 401) {
      // The server forgot this device (fresh database): register again.
      api.forgetDevice();
      await api.ensureDevice();
      got = await api.getProfile();
    }
    if (got.status !== 200) return 'offline';
    if (!got.profile) {
      // First online start: the local save becomes the initial profile (accepted once), unless
      // there is nothing worth keeping.
      const hasLocal = store.status === 'loaded' || store.status === 'migrated';
      got = hasLocal ? await api.importProfile(store.exportJson()) : await api.createProfile(store.profile.mainClassId);
      if (got.status === 409) got = await api.getProfile();
      if (got.status !== 200 || !got.profile) return 'offline';
    }
    if (opts.cancelled?.()) return 'offline';
    return store.goOnline(api, got.profile, got.version) ? 'online' : 'offline';
  } catch (err) {
    console.warn('[online] could not connect; playing offline', err);
    return 'offline';
  }
}

/** `connectOnline` that gives up after `ms` (the game must start either way; a late answer is ignored). */
export function connectWithin(ms: number, store: ProfileStore, api: ApiClient, opts: { offline?: boolean } = {}): Promise<OnlineMode> {
  let late = false;
  const timeout = new Promise<OnlineMode>((r) =>
    setTimeout(() => {
      late = true;
      r('offline');
    }, ms),
  );
  return Promise.race([connectOnline(store, api, { ...opts, cancelled: () => late }), timeout]);
}

export interface RunTicket {
  ticket: string;
  seed: number;
  level: number;
}

/** A server ticket for a solo key (seed and clamped level from the server), or null. */
export async function startOnlineRun(api: ApiClient, req: { dungeonId: string; level: number; spectate: boolean }): Promise<RunTicket | null> {
  try {
    const r = await api.runStart(req);
    if (r.status !== 200 || typeof r.ticket !== 'string' || typeof r.seed !== 'number' || typeof r.level !== 'number') return null;
    return { ticket: r.ticket, seed: r.seed, level: r.level };
  } catch {
    return null;
  }
}

export interface FinishedOnline {
  summary: RunSummary;
  claims: AutoClaims | null;
  /** The result the server paid (clamped: handlers.clampedResult); show this, not the local one. */
  result: RunResult;
}

/**
 * Report a finished solo key. The server rebuilds and clamps the result and pays it
 * (handlers.runFinish); the store takes the server's profile. Null when it failed.
 */
export async function finishOnlineRun(
  api: ApiClient,
  store: ProfileStore,
  t: RunTicket,
  run: { result: RunResult; party: { classId: ClassId; controller: 'human' | 'bot' }[]; botPreset: BotSkillPreset | null },
): Promise<FinishedOnline | null> {
  try {
    const r = await api.runFinish({
      ticket: t.ticket,
      completed: run.result.completed,
      elapsedMs: run.result.elapsedMs,
      deaths: run.result.deaths,
      forces: run.result.forces,
      affixes: run.result.affixes,
      party: run.party,
      botPreset: run.botPreset,
      localDate: localDateString(),
    });
    if (r.status !== 200 || !r.profile) return null;
    store.acceptServer(r.profile, r.version);
    return { summary: withProfile(r.summary as RunSummary, store), claims: r.claims ? withProfile(r.claims as AutoClaims, store) : null, result: (r.result as RunResult | undefined) ?? run.result };
  } catch {
    return null;
  }
}

/** Server summaries come without profiles (they carry the seed); point them at the store's. */
export function withProfile<T extends { profile: unknown }>(x: T, store: ProfileStore): T {
  return { ...x, profile: store.profile };
}
